import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import { AIChatSessionSummary } from 'models/AiDataModels';

import { ApiRouteService } from 'core/api/services';
// Assuming this path is correct
import { ProgressSpinnerOverlay } from 'core/ui';

import { useApiClient } from 'features/api';

// For loading state

const SummaryContainer = styled.div`
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const SessionItem = styled.div`
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  h4 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    color: #333;
  }

  p {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    color: #555;
  }
`;

const FormattedAIOutput = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  color: #333;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 600;
  }

  h1 {
    font-size: 1.25rem;
  }
  h2 {
    font-size: 1.125rem;
  }
  h3 {
    font-size: 1rem;
  }
  h4,
  h5,
  h6 {
    font-size: 0.875rem;
  }

  p {
    margin-bottom: 0.75rem;
    color: #555;
  }

  ul,
  ol {
    margin-bottom: 0.75rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  strong,
  b {
    font-weight: 600;
    color: #2c3e50;
  }

  em,
  i {
    font-style: italic;
  }

  code {
    background-color: #f1f3f4;
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.85em;
  }

  .summary-section {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background-color: #fff;
    border-left: 4px solid #007bff;
    border-radius: 0 4px 4px 0;
  }

  .key-findings {
    background-color: #fff3cd;
    border-left-color: #ffc107;
  }

  .recommendations {
    background-color: #d1ecf1;
    border-left-color: #17a2b8;
  }
`;

const RawDataToggle = styled.button`
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: #6c757d;
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    background-color: #f8f9fa;
    border-color: #adb5bd;
  }
`;

const RawDataContainer = styled.pre`
  background-color: #f0f0f0;
  padding: 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 0.5rem;
  border: 1px solid #dee2e6;
`;

const ErrorMessage = styled.div`
  color: red;
  padding: 1rem;
  border: 1px solid red;
  border-radius: 4px;
  background-color: #ffeeee;
`;

interface AISummaryProps {
  patientId: number;
}

// Helper function to format AI output text
const formatAIOutput = (text: string): string => {
  if (!text) return '';

  // Basic markdown-like formatting
  let formatted = text
    // Headers
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    // Bold text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic text
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap in paragraphs
  if (formatted && !formatted.startsWith('<h') && !formatted.startsWith('<p>')) {
    formatted = '<p>' + formatted + '</p>';
  }

  // Handle lists (basic implementation)
  formatted = formatted.replace(/^[-*+] (.+)$/gm, '<li>$1</li>');
  formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  return formatted;
};

// Helper function to detect and structure common AI summary sections
const structureAIContent = (text: string): string => {
  if (!text) return '';

  let structured = text;

  // Detect common medical summary sections
  const sectionPatterns = [
    { pattern: /(Summary|SUMMARY|Overview|OVERVIEW)[:]\s*(.*?)(?=\n\n|\n[A-Z]|$)/gs, className: 'summary-section' },
    { pattern: /(Key Findings|KEY FINDINGS|Findings|FINDINGS)[:]\s*(.*?)(?=\n\n|\n[A-Z]|$)/gs, className: 'summary-section key-findings' },
    { pattern: /(Recommendations|RECOMMENDATIONS|Conclusion|CONCLUSION)[:]\s*(.*?)(?=\n\n|\n[A-Z]|$)/gs, className: 'summary-section recommendations' },
  ];

  sectionPatterns.forEach(({ pattern, className }) => {
    structured = structured.replace(pattern, (match, title, content) => {
      return `<div class="${className}"><strong>${title}:</strong><br>${content.trim()}</div>`;
    });
  });

  return formatAIOutput(structured);
};

const AISummary: React.FC<AISummaryProps> = ({ patientId }) => {
  const apiClient = useApiClient();
  const [sessions, setSessions] = useState<AIChatSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRawData, setExpandedRawData] = useState<Set<number>>(new Set());

  const toggleRawData = (sessionId: number) => {
    setExpandedRawData((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!patientId) {
      setIsLoading(false);
      setError('Patient ID is not provided.');
      return;
    }

    const fetchAiSessions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const options = await apiClient.httpClient.createStandardOptions('GET', null);
        const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/parseddata/patients/${patientId}/ai-chat-sessions`;
        const response = await apiClient.httpClient.fetchAndParse<AIChatSessionSummary[]>(fetch(url, options));

        setSessions(response || []);
      } catch (err) {
        console.error('Error fetching AI chat sessions:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAiSessions();
  }, [patientId, apiClient]);

  if (isLoading) {
    return <ProgressSpinnerOverlay show={true} />;
  }

  if (error) {
    return <ErrorMessage>Error loading AI Summary: {error}</ErrorMessage>;
  }

  if (sessions.length === 0) {
    return (
      <SummaryContainer>
        <h2>AI Chat Session Summaries</h2>
        <p>No AI chat sessions found for this patient.</p>
      </SummaryContainer>
    );
  }

  return (
    <SummaryContainer>
      <h2>AI Chat Session Summaries</h2>
      {sessions.map((session) => (
        <SessionItem key={session.id}>
          <h4>
            Session ID: {session.id} {session.fileNameHint && `(${session.fileNameHint})`}
          </h4>
          <p>
            <strong>Date:</strong> {new Date(session.dateCreated).toLocaleString()}
          </p>
          <p>
            <strong>Status:</strong> {session.status}
          </p>
          {session.aiModelUsed && (
            <p>
              <strong>AI Model:</strong> {session.aiModelUsed}
            </p>
          )}
          {session.aiOutputPreview && (
            <>
              <p>
                <strong>AI Analysis:</strong>
              </p>
              <FormattedAIOutput
                dangerouslySetInnerHTML={{
                  __html: structureAIContent(session.aiOutputPreview),
                }}
              />
              <RawDataToggle onClick={() => toggleRawData(session.id)}>{expandedRawData.has(session.id) ? 'Hide Raw Data' : 'Show Raw Data'}</RawDataToggle>
              {expandedRawData.has(session.id) && <RawDataContainer>{session.aiOutputPreview}</RawDataContainer>}
            </>
          )}
          {session.hasJsonOutput && session.jsonOutputPreview && (
            <>
              <p>
                <strong>Structured Data:</strong>
              </p>
              <RawDataContainer>{session.jsonOutputPreview}</RawDataContainer>
            </>
          )}
          {session.inputMessagePreview && (
            <>
              <p>
                <strong>Input Preview:</strong>
              </p>
              <RawDataContainer>{session.inputMessagePreview}</RawDataContainer>
            </>
          )}
          {session.errorMessage && (
            <>
              <p>
                <strong>Error:</strong>
              </p>
              <RawDataContainer style={{ color: 'red' }}>{session.errorMessage}</RawDataContainer>
            </>
          )}
        </SessionItem>
      ))}
    </SummaryContainer>
  );
};

export default AISummary;
